import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { endOfMonth, startOfMonth } from "date-fns";
import moment from "moment";
import { toast } from "react-toastify";
import {
  useGetCertificateReportTableDataQuery,
  useGetOtherReportBasicDataQuery,
  useUpdateInternalReportTableHeadDataMutation,
} from "../../../store/queries/Reports";
import { updateConfig } from "../../../store/slices/Reports/reportsSlice";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { downloadCertificateReport } from "../api";
import getFilterCount from "../../../utils/getFilterCount";

const useCertificateReport = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  const dispatch = useDispatch();
  const reportState = useSelector((state) => state.reports);
  const certificateState = reportState?.certificateReport;
  const { tableFields, showEditModal } = useSelector((state) => state.global);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [date, setDate] = useState({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
    key: "selection",
  });

  const startDate = new Date(date.startDate).toISOString().split("T")[0];
  const endDate = new Date(date.endDate).toISOString().split("T")[0];
  const [formattedDate, setFormattedDate] = useState([startDate, endDate]);

  const formik = useFormik({
    initialValues: {
      order_type: "",
    },
  });

  const { data: basicData = {}, isLoading: isBasicDataLoading } =
    useGetOtherReportBasicDataQuery({});

  const orderTypes = useMemo(
    () => basicData?.data?.order_types ?? [],
    [basicData]
  );

  // the first order type is selected by default once the basic data lands
  useEffect(() => {
    if (!formik.values.order_type && orderTypes?.length > 0) {
      formik.setFieldValue("order_type", orderTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTypes]);

  const params = {
    per_page: certificateState?.currentPageSize,
    page: certificateState?.currentPage,
    sort_by: certificateState?.sortBy,
    sort_order: certificateState?.sortOrder,
    search: certificateState?.search,
    date: formattedDate,
    order_type: formik?.values?.order_type?.id,
    shop: certificateState?.filter?.shop ?? [],
    supplier: certificateState?.filter?.supplier ?? [],
    item_type: certificateState?.filter?.item_type ?? [],
  };

  const {
    data: certificateReportData = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetCertificateReportTableDataQuery(params, {
    skip: isBasicDataLoading,
  });

  const [updateReportFields] = useUpdateInternalReportTableHeadDataMutation();

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = certificateReportData?.data?.fields;
      })
    );
    // eslint-disable-next-line
  }, [certificateReportData]);

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => {
        const value = data[field];
        if (Array.isArray(value)) {
          return <p className="pro-mb-0">{value.join(", ") || "-"}</p>;
        }
        return <p className="pro-mb-0">{value || "-"}</p>;
      },
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleDateChange = (date) => {
    const startDateUTC = new Date(date.startDate);
    const endDateUTC = new Date(date.endDate);
    const timeZoneOffset = 5.5 * 60 * 60 * 1000;
    const startDateIST = new Date(startDateUTC.getTime() + timeZoneOffset);
    const endDateIST = new Date(endDateUTC.getTime() + timeZoneOffset);

    const formatDate = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setFormattedDate([formatDate(startDateIST), formatDate(endDateIST)]);
    dispatch(
      updateConfig((state) => {
        state.certificateReport.currentPage = 1;
      })
    );
  };

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.certificateReport.search = e.target.value;
        state.certificateReport.currentPage = 1;
      })
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.certificateReport.currentPageSize = page_size;
        state.certificateReport.currentPage = 1;
      })
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.certificateReport.currentPage = page;
      })
    );
  };

  const handleSort = (label) => {
    if (certificateState?.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.certificateReport.currentPage = 1;
          state.certificateReport.sortOrder =
            certificateState?.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.certificateReport.currentPage = 1;
          state.certificateReport.sortBy = label;
          state.certificateReport.sortOrder = "asc";
        })
      );
    }
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleFilterClick = () => {
    dispatch(
      updateConfig((state) => {
        state.certificateReport.showFilter =
          !state.certificateReport.showFilter;
      })
    );
  };

  const filterCount = useMemo(() => {
    return getFilterCount({
      shop: certificateState?.filter?.shop,
      supplier: certificateState?.filter?.supplier,
      item_type: certificateState?.filter?.item_type,
    });
  }, [certificateState?.filter]);

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.certificateReport.search = "";
        state.certificateReport.currentPage = 1;
        state.certificateReport.sortBy = "";
        state.certificateReport.sortOrder = "";
        state.certificateReport.filter.shop = [];
        state.certificateReport.filter.supplier = [];
        state.certificateReport.filter.item_type = [];
      })
    );
    setFormattedDate([startDate, endDate]);
    formik.setFieldValue("order_type", orderTypes?.[0] ?? "");
  };

  const handleExport = () => {
    setLoading(true);
    downloadCertificateReport(params).then((response) => {
      setLoading(false);
      if (response?.status === 200) {
        const dataType = response?.data?.type;
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(
          new Blob([response?.data], { type: dataType })
        );
        downloadLink.setAttribute(
          "download",
          `Certificate Report_${moment(Date.now()).format(
            "DD_MM_YYYY"
          )}_${moment(Date.now()).format("HH_mm_ss")}.xlsx`
        );
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } else {
        toast.error("Something went wrong");
      }
    });
  };

  return {
    date,
    formik,
    loading,
    isLoading,
    isFetching,
    basicData,
    orderTypes,
    tableFields,
    showEditModal,
    filterCount,
    certificateState,
    certificateReportData,
    paginationOptions,
    getRow,
    refetch,
    handleSort,
    handleSearch,
    handleExport,
    handlePageSize,
    handlePagination,
    handleDateChange,
    handleFilterClick,
    handleClearClick,
    updateReportFields,
    handleEditColumnsClick,
  };
};

export default useCertificateReport;
